import { Component, Input, OnInit } from '@angular/core';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import {
  BookingDetails,
  BookingDetailsCompletedPayment,
  BookingDetailsParticipant
} from '../../../../../../models/scheduled-event';
import { getBookingStatusDisplay } from '../../../../../../models/booking-enums';
import { getPaymentMethodTypeDisplay } from '../../../../../../models/payment';
import { getDetailsEventLink } from '../event-details-link';
import { ScheduledEventService } from '../../../../../../services/scheduled-event/scheduled-event.service';
import { OrganizationConfigService } from '../../../../../../services/organization-config/organization-config.service';
import {
  dateStringFromISO8601,
  getAge,
  propExistsAndIsNonEmpty,
  timeStringFromISO8601
} from '../../../../../../utils';
import { PAYMENT_DETAILS_QUERY_PARAM } from '../../../payments/modals/payment-details-modal/payment-details-modal.component';
import { formatQuestionAnswer, getQuestionNames } from '../question-responses';
import {
  BOOKING_DETAILS_QUERY_PARAM,
  RESERVATION_DETAILS_QUERY_PARAM
} from '../details-modal-query-params';


export { BOOKING_DETAILS_QUERY_PARAM };


@Component({
  selector: 'app-booking-details-modal',
  templateUrl: './booking-details-modal.component.html',
  styleUrls: ['./booking-details-modal.component.css']
})
export class BookingDetailsModalComponent implements OnInit {

  // The booking whose details are shown.
  @Input() bookingId: number;

  public details: BookingDetails = null;
  public loading = false;
  public serverError: number = null;

  public readonly getAge = getAge;
  public readonly dateStringFromISO8601 = dateStringFromISO8601;
  public readonly timeStringFromISO8601 = timeStringFromISO8601;
  public readonly propExistsAndIsNonEmpty = propExistsAndIsNonEmpty;
  public readonly getPaymentMethodTypeDisplay = getPaymentMethodTypeDisplay;

  public readonly getQuestionNames = getQuestionNames;
  public readonly formatQuestionAnswer = formatQuestionAnswer;


  constructor(
    public activeModal: NgbActiveModal,
    public orgConfigService: OrganizationConfigService,
    private scheduledEventService: ScheduledEventService
  ) {}

  ngOnInit() {
    this.loadDetails();
  }

  public loadDetails() {
    this.loading = true;
    this.serverError = null;

    this.scheduledEventService
      .getBookingDetails(this.bookingId)
      .then(details => {
        this.details = details;
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.loading = false; });
  }

  /** The event details page, or null when the event cannot be addressed. */
  public getEventDetailsLink(): string | null {
    return getDetailsEventLink(this.details);
  }

  public getStatusDisplay(): string {
    if (!this.details) {
      return '';
    }
    return getBookingStatusDisplay(this.details.status);
  }

  /**
   * How the booking came to exist, as one sentence. The cases are ordered by
   * how much they explain: a roster or a private-event request accounts for the
   * booking entirely, and the staff user only tells us who typed it in.
   */
  public getCreatedByText(): string {
    if (!this.details) {
      return '';
    }
    if (this.details.roster_membership_id) {
      return 'Roster membership: ' + this.details.roster_membership_name;
    }
    if (this.details.linked_private_event_id) {
      return 'Private event request';
    }
    if (this.details.is_staff_direct_booking) {
      return 'Staff direct booking by ' + this.getStaffUserDisplay();
    }
    if (this.details.staff_user_email ||
        this.details.staff_user_first_name ||
        this.details.staff_user_last_name) {
      return "By staff on the customer's behalf: " + this.getStaffUserDisplay();
    }
    return 'By the customer in the Climber App';
  }

  /**
   * Whether the Created By sentence carries a link, and so must be rendered in
   * pieces rather than as plain text.
   */
  public getCreatedByLink(): string | null {
    if (!this.details) {
      return null;
    }
    if (this.details.roster_membership_id) {
      return this.details.booking_customer_id
        ? '/customers/' + this.details.booking_customer_id + '/memberships-and-passes'
        : null;
    }
    if (this.details.linked_private_event_id &&
        this.details.linked_private_event_first_part_id) {
      return '/events/scheduled/now/event/' +
        this.details.linked_private_event_first_part_id;
    }
    return null;
  }

  /** "First Last (email)", with the same fallbacks as getStaffNameDisplay. */
  public getStaffUserDisplay(): string {
    return this.formatNameAndEmail(
      [this.details.staff_user_first_name, this.details.staff_user_last_name]
        .filter(part => !!part).join(' '),
      this.details.staff_user_email
    );
  }

  /**
   * A bypassing staff user. The payload gives a joined name plus an email
   * rather than the split fields getStaffNameDisplay takes.
   */
  public formatNameAndEmail(name: string | null, email: string | null): string {
    const trimmedName = (name || '').trim();
    if (!trimmedName && !email) {
      return '(no name)';
    }
    if (!trimmedName) {
      return email;
    }
    return email ? trimmedName + ' (' + email + ')' : trimmedName;
  }

  public getParticipantQueryParams(
      participant: BookingDetailsParticipant): { [key: string]: number } {
    return { [RESERVATION_DETAILS_QUERY_PARAM]: participant.reservation_id };
  }

  public getPaymentQueryParams(
      payment: BookingDetailsCompletedPayment): { [key: string]: number } {
    return { [PAYMENT_DETAILS_QUERY_PARAM]: payment.id };
  }

  /** Money arrives as a two-place decimal string. */
  public formatMoney(amount: string): string {
    return this.orgConfigService.formatCurrency(parseFloat(amount));
  }

  public amountIsPositive(amount: string): boolean {
    return parseFloat(amount) > 0;
  }

  /**
   * The participant's name, or null when the booking holds no name for them —
   * a private event request's placeholder reservation, which would otherwise
   * render as an empty cell.
   */
  public getParticipantName(participant: BookingDetailsParticipant): string | null {
    const name = (participant.first_name + ' ' + participant.last_name).trim();

    return name === '' ? null : name;
  }

  public getCheckInsDisplay(participant: BookingDetailsParticipant): string {
    if (participant.part_count > 1) {
      return participant.checked_in_part_count + ' / ' + participant.part_count;
    }
    return participant.checked_in_part_count > 0 ? 'Checked in' : 'Not checked in';
  }
}
